import React, { FC, useCallback, useEffect, useMemo } from 'react';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { useQueryParam } from 'use-query-params';
import { useMutation, useQuery } from '@apollo/client';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Menu from 'vibo-ui/Menu';
import Button from 'vibo-ui/Button';
import Dropdown from 'vibo-ui/Dropdown';
import Scrollbar from 'vibo-ui/Scrollbar';
import PlaylistCard from './PlaylistCard';
import SearchInput from 'vibo-ui/SearchInput';
import CreateButton from 'components/buttons/CreateButton';
import NoIdeasYet from 'components/emptyStates/NoIdeasYet';
import LoadingOverlay from 'components/common/LoadingOverlay';
import EventTitle from 'components/events/Timeline/EventTitle';
import PageContentHeader from 'components/common/PageContentHeader';
import NoIdeasSearchResults from 'components/emptyStates/NoIdeasSearchResults';
import SongIdeasCardsViewComponent from 'components/songs/SongsFinder/SongIdeasCardsView/SongIdeasCardsView.Component';
import Icon, { IconmoonFont } from 'vibo-ui/Icon';

import { gridProps } from './constants';
import { useModal } from 'vibo-ui/Modal';
import { onError } from 'graphql/helpers';
import { toastNotify } from 'graphql/hooks/common';
import { DEFAULT_PAGINATION } from 'services/constants';
import { usePrevious } from 'services/common/dataProcessingHelpers';
import { getRedirectPlaylistLink, getSongIdeasData } from 'services/songIdeas/helpers';

import { CLONE_SONG_IDEAS } from 'graphql/mutations/songIdeas';

import { SongIdeasListProps } from './interfaces';
import { ButtonIconType, Modals, SongIdeasType } from 'types/enums';
import { DeleteSongIdeasProps } from 'components/modals/DeleteSongIdeasModal';
import { CloneModalProps, ClonePayloadProps } from 'components/modals/CloneModal';
import { CreatePlaylistModalProps } from 'components/modals/CreatePlaylistModal';

const SongIdeasList: FC<SongIdeasListProps> = ({ query, queryKey }) => {
  const { t } = useTranslation();

  const { type = '' } = useParams<{ type: string }>();

  const location = useLocation();
  const navigate = useNavigate();

  const { openModal } = useModal();

  const openCreatePlaylistModal = (playlist?: SongIdeasPreview) =>
    openModal<CreatePlaylistModalProps>({
      key: Modals.createPlaylist,
      props: playlist && {
        editMode: true,
        data: {
          title: playlist?.title,
          description: playlist.description || '',
          id: playlist._id,
          coverPhoto:
            playlist?.coverPhoto?.cropped?.medium ||
            playlist?.coverPhoto?.url ||
            playlist?.coverPhoto?.cropped?.small,
        },
      },
    });
  const openCloneModal = (playlist: SongIdeasPreview) =>
    openModal<CloneModalProps>({
      key: Modals.clone,
      props: {
        values: {
          title: playlist.title,
        },
        onSubmit: (payload: ClonePayloadProps) => {
          cloneSongIdeas({
            variables: {
              songIdeasId: playlist._id,
              payload,
            },
          });
        },
        title: t('clonePlaylist'),
        label: t('playlistName'),
        closeOnSubmit: true,
      },
    });
  const openDeletePlaylistModal = (playlist: SongIdeasPreview) =>
    openModal<DeleteSongIdeasProps>({
      key: Modals.deletePlaylist,
      props: {
        playlistTitle: playlist.title,
        playlistId: playlist._id,
        refetchQueries: ['getUserSongIdeas'],
      },
    });

  const [q = '', setQ] = useQueryParam<string | undefined>('q');

  const prevPlaylistsType = usePrevious(type);

  const { data, error, fetchMore, loading } = useQuery<SongIdeasResponse, SongIdeasListVariables>(
    query,
    {
      variables: {
        filter: { q },
        pagination: DEFAULT_PAGINATION,
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  );
  const songIdeasData: SongIdeasResponseData = getSongIdeasData(data);

  const [cloneSongIdeas] = useMutation(CLONE_SONG_IDEAS, {
    onCompleted: ({ cloneSongIdeas }) => {
      toastNotify({
        text: t('playlistWasCopiedToMyIdeasSuccessfully'),
        iconType: ButtonIconType.copy,
      });
      navigate(getRedirectPlaylistLink(cloneSongIdeas._id), {
        state: location.state,
      });
    },
    onError,
    refetchQueries: ['getUserSongIdeas'],
  });

  const songIdeasNames = useMemo(
    () => ({
      [SongIdeasType.me]: 'youHavePlaylists',
      [SongIdeasType.vibo]: 'publicPlaylistsAvailable',
    }),
    []
  );

  const pageName = get(songIdeasNames, type);

  const openSongIdeas = (songIdeasId: string) => navigate(`/songIdeas/v/${songIdeasId}`);

  const loadMore = useCallback(() => {
    if (!loading && !!songIdeasData.next) {
      fetchMore({
        variables: {
          filter: { q },
          pagination: songIdeasData.next,
        },
        updateQuery: (prev: any, { fetchMoreResult }: any) => {
          if (!fetchMoreResult) return prev;

          return {
            [queryKey]: {
              ...prev[queryKey],
              songIdeas: [...prev[queryKey].songIdeas, ...fetchMoreResult[queryKey].songIdeas],
              next: fetchMoreResult[queryKey].next,
            },
          };
        },
      });
    }
  }, [q, loading, songIdeasData.next?.limit, songIdeasData.next?.skip]);

  useEffect(() => {
    setQ(undefined);

    return () => setQ(undefined);
  }, []);

  if (error) {
    return null;
  }

  return (
    <>
      <PageContentHeader
        extra={
          <>
            <SearchInput placeholder={t('searchPlaylists')} loading={loading} />
            <Button
              onClick={() => openCreatePlaylistModal()}
              prefixIcon={IconmoonFont['plus-16']}
              displayType="bordered"
              shape="round"
              size="lg"
            >
              {t('createNewPlaylist')}
            </Button>
          </>
        }
      >
        <EventTitle name={t(pageName, { count: songIdeasData.totalCount })} />
      </PageContentHeader>
      {!data && loading ? (
        <LoadingOverlay />
      ) : !songIdeasData.totalCount ? (
        !q ? (
          <NoIdeasYet>
            <CreateButton
              onClick={() => openCreatePlaylistModal()}
              text={t('createNewPlaylist')}
              className="centrate"
            />
            <Button
              to="/songIdeas/vibo"
              prefixIcon={IconmoonFont['plus-16']}
              className="centrate"
              displayType="bordered"
              shape="round"
              size="lg"
            >
              {t('addFromPublicPlaylist')}
            </Button>
          </NoIdeasYet>
        ) : (
          <NoIdeasSearchResults />
        )
      ) : prevPlaylistsType !== type && loading ? null : (
        <Scrollbar id="song-finder-scroll">
          <SongIdeasCardsViewComponent
            renderPlaylistCard={playlist => (
              <PlaylistCard
                onClick={() => openSongIdeas(playlist._id)}
                playlist={playlist}
                actionButton={
                  <Dropdown
                    overlay={
                      <Menu
                        onClick={({ domEvent }) => domEvent.stopPropagation()}
                        selectable={false}
                      >
                        <Menu.Item
                          onClick={() => openCloneModal(playlist)}
                          icon={IconmoonFont['clone-16']}
                          key="clone-playlist-btn"
                        >
                          {t('cloneToPlaylists')}
                        </Menu.Item>
                        {playlist.isOwner ? (
                          <Menu.Item
                            onClick={() => openCreatePlaylistModal(playlist)}
                            icon={IconmoonFont['editPencil-16']}
                            key="edit-playlist-btn"
                          >
                            {t('editPlaylist')}
                          </Menu.Item>
                        ) : null}
                        {playlist.isOwner ? (
                          <Menu.Item
                            onClick={() => openDeletePlaylistModal(playlist)}
                            icon={IconmoonFont['delete-16']}
                            key="delete-playlist-btn"
                          >
                            {t('deletePlaylist')}
                          </Menu.Item>
                        ) : null}
                      </Menu>
                    }
                    trigger="click"
                    withModalOverlay={false}
                  >
                    <Icon icon={IconmoonFont['threeDots-16']} />
                  </Dropdown>
                }
                key={playlist._id}
              />
            )}
            playlists={songIdeasData.songIdeas}
            totalCount={songIdeasData.totalCount}
            loadMore={loadMore}
            loading={loading}
            gridProps={gridProps}
            hasMore={!!songIdeasData.next}
          />
        </Scrollbar>
      )}
    </>
  );
};

export default SongIdeasList;
